import React, {
  useEffect,
  useState,
} from "react";

import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";

import "../styles/ShopOrders.css";


/* =====================================================
   ADMIN UID
===================================================== */

const ADMIN_UID =
  "jKahzGJWb2hRmWWrppjWCiazpFd2";


/* =====================================================
   SHOP OWNER STATUS OPTIONS

   Shop owner can control:

   New
   Processing
   Packed
   Ready for delivery
   Cancelled

   Delivered is controlled ONLY
   by customer confirmation after
   delivery person marks delivery completed.
===================================================== */

const SHOP_STATUS_OPTIONS = [
  "New",
  "Processing",
  "Packed",
  "Ready for delivery",
  "Cancelled",
];


/* =====================================================
   DISPLAY FILTERS
===================================================== */

const ALL_STATUS_OPTIONS = [
  "New",
  "Processing",
  "Packed",
  "Ready for delivery",
  "Delivered",
  "Cancelled",
];


export default function ShopOrders() {

  const {
    user,
    userProfile,
  } = useAuth();


  const [filter, setFilter] =
    useState("All");

  const [orders, setOrders] =
    useState([]);

  const [loading, setLoading] =
    useState(true);


  /* =====================================================
     ADMIN CHECK
  ===================================================== */

  const isAdmin =
    user?.uid === ADMIN_UID ||
    userProfile?.role === "admin";


  /* =====================================================
     UPDATE SHOP ORDER STATUS
  ===================================================== */

  const updateStatus = async (
    orderId,
    status
  ) => {

    if (
      !orderId ||
      !status
    ) {
      return;
    }


    /* =================================================
       DELIVERED CANNOT BE SET BY SHOP OWNER
       
       Customer must confirm receipt.
    ================================================= */

    if (
      status ===
      "Delivered"
    ) {

      alert(
        "Delivered status can only be confirmed by the customer after receiving the order."
      );

      return;
    }


    try {

      /*
       * =================================================
       * SHOP ORDER REFERENCE
       * =================================================
       */

      const orderRef =
        doc(
          db,
          "shopOrders",
          orderId
        );


      /*
       * =================================================
       * GET CURRENT ORDER FROM LOCAL STATE
       * =================================================
       */

      const order =
        orders.find(
          (item) =>
            item.id ===
            orderId
        );


      if (!order) {

        throw new Error(
          "Order details not found."
        );

      }


      /* =================================================
         READY FOR DELIVERY
         
         This is the important delivery assignment step.
      ================================================= */

      if (
        status ===
        "Ready for delivery"
      ) {


        /* ===============================================
           IMPORTANT

           The shopOrders Firestore document ID
           is used as the deliveryRequests document ID.

           DECLARE THIS BEFORE USING IT.
        =============================================== */

        const shopOrderDocId =
          order.id;


        /* ===============================================
           DELIVERY PERSON
        =============================================== */

        const deliveryPersonUid =
          order.deliveryPersonUid ||
          "";

        const deliveryPersonName =
          order.deliveryPersonName ||
          "";

        const deliveryPersonMobile =
          order.deliveryPersonMobile ||
          "";

        const deliveryPersonVehicleType =
          order.deliveryPersonVehicleType ||
          "";

        const deliveryPersonVehicleNumber =
          order.deliveryPersonVehicleNumber ||
          "";

        const deliveryPersonArea =
          order.deliveryPersonArea ||
          "";

        const deliveryPersonPhoto =
          order.deliveryPersonPhoto ||
          order.deliveryPersonPhotoURL ||
          "";


        /* ===============================================
           DELIVERY PERSON REQUIRED
        =============================================== */

        if (
          !deliveryPersonUid
        ) {

          alert(
            "No delivery person is assigned to this order."
          );

          return;
        }


        /* ===============================================
           DELIVERY CHARGE
        =============================================== */

        const deliveryCharge =
          Number(
            order.deliveryCharge ||
            0
          );


        /* ===============================================
           ORDER TOTAL
           
           Preserve existing order.total.
        =============================================== */

        const orderTotal =
          Number(
            order.total ||
            0
          );


        /*
         * Customer payable total.
         */

        const customerPayableTotal =
          orderTotal +
          deliveryCharge;


        /* ===============================================
           INITIAL DELIVERY CONFIRMATION STATE
           
           Every new delivery starts as:
           
           customerReceived = false
        =============================================== */

        const customerReceived =
          false;


        /* ===============================================
           1. UPDATE SHOP ORDER
        =============================================== */

        await updateDoc(
          orderRef,
          {

            status:
              "Ready for delivery",

            deliveryStatus:
              "ASSIGNED",

            deliveryPersonUid,

            deliveryPersonName,

            deliveryPersonMobile,

            deliveryPersonVehicleType,

            deliveryPersonVehicleNumber,

            deliveryPersonArea,

            deliveryPersonPhoto,

            deliveryCharge,


            /*
             * IMPORTANT:
             * Link this shop order to the
             * delivery request.
             */

            deliveryRequestId:
              shopOrderDocId,


            /*
             * Customer has not received
             * the order yet.
             */

            customerReceived,

            customerReceivedAt:
              null,


            deliveryAssignedAt:
              serverTimestamp(),

            updatedAt:
              serverTimestamp(),

          }
        );


        /* ===============================================
           2. DELIVERY REQUEST REFERENCE
           
           SAME ID AS shopOrders DOCUMENT
        =============================================== */

        const deliveryRequestRef =
          doc(
            db,
            "deliveryRequests",
            shopOrderDocId
          );


        /* ===============================================
           3. CREATE / UPDATE DELIVERY REQUEST
        =============================================== */

        await setDoc(
          deliveryRequestRef,
          {

            /* =========================================
               SHOP ORDER REFERENCE
            ========================================= */

            shopOrderDocId,


/* =========================================
   SHOP / PICKUP DETAILS
========================================= */

shopId:
  order.shopId ||
  "",

shopName:
  order.shopName ||
  "",

shopMobile:
  order.mobile ||
  "",
            /* =========================================
               CUSTOMER ORDER ID
            ========================================= */

            orderId:
              order.orderId ||
              "",


            /* =========================================
               CUSTOMER
            ========================================= */

            customerUid:
              order.customerUid ||
              order.uid ||
              "",

            customerName:
              order.customerName ||
              "",

            customerMobile:
              order.mobile ||
              order.customerMobile ||
              "",

            address:
              order.address ||
              "",

            area:
              order.area ||
              "",


            /* =========================================
               ORDER AMOUNT
            ========================================= */

            total:
              customerPayableTotal,

            deliveryCharge:
              deliveryCharge,


            /* =========================================
               PAYMENT
            ========================================= */

            paymentType:
              order.paymentType ||
              "Cash on Delivery",


            /* =========================================
               ITEMS
            ========================================= */

            items:
              order.items ||
              [],


            /* =========================================
               DELIVERY PERSON
            ========================================= */

            deliveryPersonUid,

            deliveryPersonName,

            deliveryPersonMobile,

            deliveryPersonVehicleType,

            deliveryPersonVehicleNumber,

            deliveryPersonArea,

            deliveryPersonPhoto,


            /* =========================================
               DELIVERY REQUEST STATUS
               
               ASSIGNED means:
               Request is waiting for delivery
               person to accept.
            ========================================= */

            status:
              "ASSIGNED",

            deliveryStatus:
              "ASSIGNED",


            /* =========================================
               CUSTOMER RECEIPT
            ========================================= */

            customerReceived:
              false,

            customerReceivedAt:
              null,


            /* =========================================
               TIMESTAMPS
            ========================================= */

            assignedAt:
              serverTimestamp(),

            updatedAt:
              serverTimestamp(),

          },

          {
            merge:
              true,
          }
        );


        console.log(
          "Delivery request created:",
          shopOrderDocId
        );


        /*
         * Stop here.
         *
         * We already updated the shop order
         * and created the delivery request.
         */

        return;
      }


      /* =================================================
         NORMAL SHOP STATUS UPDATE
         
         Allowed:
         
         New
         Processing
         Packed
         Cancelled
      ================================================= */

      await updateDoc(
        orderRef,
        {

          status,

          updatedAt:
            serverTimestamp(),

        }
      );


      console.log(
        "Shop order status updated:",
        orderId,
        status
      );


    } catch (error) {

      console.error(
        "Status update failed:",
        error
      );


      alert(
        error.message ||
        "Failed to update order status."
      );

    }

  };


  /* =====================================================
     LOAD SHOP ORDERS

     ADMIN
       → ALL shopOrders

     SHOP OWNER
       → THEIR OWN shopOrders

     REAL-TIME LISTENER
  ===================================================== */

  useEffect(() => {

    if (!user) {

      setOrders([]);

      setLoading(false);

      return;

    }


    setLoading(true);


    let ordersQuery;


    /* =================================================
       ADMIN
    ================================================= */

    if (isAdmin) {

      ordersQuery =
        collection(
          db,
          "shopOrders"
        );

    }


    /* =================================================
       SHOP OWNER
    ================================================= */

    else {

      ordersQuery =
        query(
          collection(
            db,
            "shopOrders"
          ),

          where(
            "ownerUid",
            "==",
            user.uid
          )
        );

    }


    /* =================================================
       REAL-TIME FIRESTORE LISTENER
    ================================================= */

    const unsubscribe =
      onSnapshot(

        ordersQuery,

        (snapshot) => {

          const list =
            snapshot.docs.map(
              (orderDoc) => ({

                id:
                  orderDoc.id,

                ...orderDoc.data(),

              })
            );


          /* =========================================
             GET TIMESTAMP
          ========================================= */

          const getTime =
            (value) => {

              if (!value) {
                return 0;
              }


              /* Firestore Timestamp */

              if (
                typeof value.toDate ===
                "function"
              ) {

                return value
                  .toDate()
                  .getTime();

              }


              /* Firestore timestamp object */

              if (
                value.seconds
              ) {

                return (
                  value.seconds *
                  1000
                );

              }


              /* JavaScript date/string */

              if (
                typeof value ===
                "string"
              ) {

                return (
                  new Date(
                    value
                  ).getTime() ||
                  0
                );

              }


              return 0;

            };


          /* =========================================
             LATEST FIRST
          ========================================= */

          list.sort(
            (a, b) =>
              getTime(
                b.createdAt
              ) -
              getTime(
                a.createdAt
              )
          );


          setOrders(list);

          setLoading(false);

        },


        (error) => {

          console.error(
            "Orders loading error:",
            error
          );

          setOrders([]);

          setLoading(false);

        }

      );


    return unsubscribe;

  }, [
    user,
    isAdmin,
  ]);


  /* =====================================================
     FILTERED ORDERS
  ===================================================== */

  const filteredOrders =
    filter === "All"
      ? orders
      : orders.filter(
          (order) =>
            (
              order.status ||
              "New"
            ) === filter
        );


  /* =====================================================
     PARSE ITEMS
  ===================================================== */

  const getItems =
    (items) => {

      if (!items) {
        return [];
      }


      try {

        const parsed =
          typeof items ===
          "string"
            ? JSON.parse(items)
            : items;


        return Array.isArray(
          parsed
        )
          ? parsed
          : [];

      } catch {

        return [];

      }

    };


  /* =====================================================
     FORMAT DATE
  ===================================================== */

  const formatDate =
    (timestamp) => {

      if (!timestamp) {
        return "";
      }


      try {

        if (
          typeof timestamp.toDate ===
          "function"
        ) {

          return timestamp
            .toDate()
            .toLocaleString();

        }


        if (
          timestamp.seconds
        ) {

          return new Date(
            timestamp.seconds *
            1000
          ).toLocaleString();

        }


        if (
          typeof timestamp ===
          "string"
        ) {

          return new Date(
            timestamp
          ).toLocaleString();

        }

      } catch {

        return "";

      }


      return "";

    };


  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {

    return (

      <div
        className="shop-orders-page"
      >
        Loading...
      </div>

    );

  }


  /* =====================================================
     RENDER
  ===================================================== */

  return (

    <div
      className="shop-orders-page"
    >


      {/* =================================================
          TITLE
      ================================================= */}

      <h2>

        {isAdmin
          ? "Eyal Mart Orders"
          : "Shop Orders"}

      </h2>


      {/* =================================================
          FILTERS
      ================================================= */}

      <div
        className="order-filters"
      >


        {/* ===============================================
            ALL
        =============================================== */}

        <button
          className={
            filter === "All"
              ? "active"
              : ""
          }

          onClick={() =>
            setFilter("All")
          }
        >

          All ({orders.length})

        </button>


        {/* ===============================================
            STATUS FILTERS
        =============================================== */}

        {ALL_STATUS_OPTIONS.map(
          (status) => {

            const count =
              orders.filter(
                (order) =>
                  (
                    order.status ||
                    "New"
                  ) === status
              ).length;


            return (

              <button
                key={status}

                className={
                  filter === status
                    ? "active"
                    : ""
                }

                onClick={() =>
                  setFilter(status)
                }
              >

                {status} ({count})

              </button>

            );

          }
        )}

      </div>


      {/* =================================================
          NO ORDERS
      ================================================= */}

      {filteredOrders.length ===
        0 && (

        <div
          style={{
            padding:
              "30px 10px",
            textAlign:
              "center",
            color:
              "#777",
          }}
        >

          No orders found.

        </div>

      )}


      {/* =================================================
          ORDER LIST
      ================================================= */}

      {filteredOrders.map(
        (order) => {

          const items =
            getItems(
              order.items
            );


          const deliveryCharge =
            Number(
              order.deliveryCharge ||
              0
            );


          const total =
            Number(
              order.total ||
              0
            ) +
            deliveryCharge;


          const isDelivered =
            order.status ===
            "Delivered";


          const deliveryCompleted =
            order.deliveryStatus ===
            "DELIVERED";


          const customerReceived =
            order.customerReceived ===
            true;


          return (

            <div
              className="order-card"
              key={order.id}
            >


              {/* =========================================
                  SHOP / ORDER HEADER
              ========================================= */}

              <div
                className="order-header"
              >

                <div>

                  <h3>

                    {order.shopName ||
                      order.shop ||
                      "Eyal Mart"}

                  </h3>


                  <div
                    className="order-id"
                  >

                    {
                      order.orderId ||
                      order.id
                    }

                  </div>


                  <div
                    className="order-date"
                  >

                    {
                      formatDate(
                        order.createdAt
                      )
                    }

                  </div>

                </div>


                {/* STATUS */}

                <div>

                  {isDelivered ? (

                    <div
                      style={{
                        padding:
                          "8px 12px",
                        borderRadius:
                          20,
                        background:
                          "#dcfce7",
                        color:
                          "#166534",
                        fontWeight:
                          700,
                        fontSize:
                          13,
                      }}
                    >
                      Delivered
                    </div>

                  ) : (

                    <select
                      value={
                        order.status ||
                        "New"
                      }

                      onChange={(event) =>
                        updateStatus(
                          order.id,
                          event.target.value
                        )
                      }

                      disabled={
                        isDelivered
                      }
                    >

                      {SHOP_STATUS_OPTIONS.map(
                        (status) => (

                          <option
                            key={status}
                            value={status}
                          >
                            {status}
                          </option>

                        )
                      )}

                    </select>

                  )}

                </div>

              </div>


              {/* =========================================
                  DELIVERY PERSON
              ========================================= */}

              {order.deliveryPersonUid && (

                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 12,
                    background:
                      "#f0fdf4",
                    border:
                      "1px solid #bbf7d0",
                  }}
                >

                  <div
                    style={{
                      fontWeight: 700,
                      marginBottom: 5,
                    }}
                  >

                    🚚 Delivery Person

                  </div>


                  <div>
                    {order.deliveryPersonName ||
                      "-"}
                  </div>


                  <div>
                    📞{" "}
                    {order.deliveryPersonMobile ||
                      "-"}
                  </div>


                  <div>
                    🛵{" "}
                    {order.deliveryPersonVehicleType ||
                      "-"}
                  </div>


                  {order.deliveryPersonVehicleNumber && (

                    <div>
                      🔢{" "}
                      {
                        order.deliveryPersonVehicleNumber
                      }
                    </div>

                  )}


                  {order.deliveryPersonArea && (

                    <div>
                      📍{" "}
                      {order.deliveryPersonArea}
                    </div>

                  )}


                  {/* =====================================
                      DELIVERY STATUS
                  ===================================== */}

                  <div
                    style={{
                      marginTop: 6,
                      fontWeight: 700,
                      color:
                        deliveryCompleted
                          ? "#16a34a"
                          : "#f59e0b",
                    }}
                  >

                    Delivery Status:{" "}

                    {
                      deliveryCompleted
                        ? "DELIVERED"
                        : (
                            order.deliveryStatus ||
                            "PENDING"
                          )
                    }

                  </div>

                </div>

              )}


              {/* =========================================
                  CUSTOMER DETAILS
              ========================================= */}

              <div
                className="customer-details"
              >

                <p>

                  <strong>
                    Customer:
                  </strong>{" "}

                  {
                    order.customerName ||
                    "-"
                  }

                </p>


                <p>

                  <strong>
                    Mobile:
                  </strong>{" "}

                  {
                    order.mobile ||
                    order.customerMobile ||
                    "-"
                  }

                </p>


                <p>

                  <strong>
                    Address:
                  </strong>

                  <br />

                  {
                    order.address ||
                    "-"
                  }

                  <br />

                  {
                    order.area ||
                    ""
                  }

                </p>

              </div>


              {/* =========================================
                  ITEMS
              ========================================= */}

              <div
                className="items"
              >

                <h4>
                  Items
                </h4>


                {items.length === 0 ? (

                  <div>
                    No items
                  </div>

                ) : (

                  items.map(
                    (item, index) => (

                      <div
                        className="item-row"
                        key={index}
                      >

                        <span>

                          {item.brand && (

                            <>
                              {item.brand}
                              {" - "}
                            </>

                          )}

                          {
                            item.name ||
                            item.productName ||
                            "Item"
                          }

                        </span>


                        <span>

                          x
                          {
                            item.qty ||
                            0
                          }

                        </span>


                        <span>

                          ₹
                          {
                            Number(
                              item.price ||
                              0
                            )
                          }

                        </span>

                      </div>

                    )
                  )

                )}

              </div>


              {/* =========================================
                  AMOUNT
              ========================================= */}

              <div
                style={{
                  borderTop:
                    "1px solid #eee",
                  paddingTop:
                    10,
                  marginTop:
                    10,
                }}
              >


                {/* DELIVERY CHARGE */}

                {deliveryCharge > 0 && (

                  <div
                    style={{
                      display:
                        "flex",
                      justifyContent:
                        "space-between",
                      marginBottom:
                        5,
                      fontSize:
                        14,
                    }}
                  >

                    <span>
                      Delivery Charge
                    </span>

                    <span>
                      ₹
                      {
                        deliveryCharge
                      }
                    </span>

                  </div>

                )}

              </div>


              {/* =========================================
                  FOOTER
              ========================================= */}

              <div
                className="order-footer"
              >

                <strong>

                  Total : ₹
                  {total}

                </strong>


                <span>

                  {
                    order.paymentType ||
                    "Cash on Delivery"
                  }

                </span>

              </div>


              {/* =========================================
                  DELIVERY PERSON DELIVERED
                  
                  WAITING FOR CUSTOMER
              ========================================= */}

              {deliveryCompleted &&
                !customerReceived &&
                !isDelivered && (

                <div
                  style={{
                    marginTop: 14,
                    padding: 14,
                    borderRadius: 12,
                    background:
                      "#fff7ed",
                    border:
                      "1px solid #fed7aa",
                  }}
                >

                  <div
                    style={{
                      fontWeight:
                        700,
                      color:
                        "#c2410c",
                      marginBottom:
                        5,
                    }}
                  >

                    📦 Delivery completed

                  </div>


                  <div
                    style={{
                      fontSize:
                        13,
                      color:
                        "#666",
                    }}
                  >

                    Waiting for the
                    customer to confirm
                    receipt.

                  </div>

                </div>

              )}


              {/* =========================================
                  CUSTOMER CONFIRMED
              ========================================= */}

              {customerReceived && (

                <div
                  style={{
                    marginTop: 14,
                    padding: 12,
                    borderRadius: 12,
                    background:
                      "#dcfce7",
                    border:
                      "1px solid #bbf7d0",
                    color:
                      "#166534",
                    fontWeight:
                      700,
                    textAlign:
                      "center",
                  }}
                >

                  ✓ Customer received
                  and confirmed the order

                </div>

              )}

            </div>

          );

        }
      )}

    </div>

  );

}